import type { DraftSessionState } from "./composerDraftStore";
import { deriveLogicalProjectKey } from "./logicalProject";
import type { Project } from "./types";

export type DraftProjectRecoveryInput = {
  readonly draftSession: Pick<
    DraftSessionState,
    "environmentId" | "projectId" | "logicalProjectKey"
  >;
  readonly environmentProjects: ReadonlyArray<Project>;
};

/**
 * Picks the project a draft should be repointed at when its stored project id
 * no longer resolves: a project sharing the draft's logical key wins, otherwise
 * the environment's only project. Returns null when the user must choose.
 */
export function resolveDraftProjectRecovery(input: DraftProjectRecoveryInput): Project | null {
  const { draftSession, environmentProjects } = input;
  const current = environmentProjects.find(
    (project) =>
      project.id === draftSession.projectId && project.environmentId === draftSession.environmentId,
  );
  if (current) {
    return null;
  }
  const byLogicalKey = environmentProjects.find(
    (project) =>
      project.environmentId === draftSession.environmentId &&
      deriveLogicalProjectKey(project) === draftSession.logicalProjectKey,
  );
  if (byLogicalKey) {
    return byLogicalKey;
  }
  const sameEnvironment = environmentProjects.filter(
    (project) => project.environmentId === draftSession.environmentId,
  );
  return sameEnvironment.length === 1 ? sameEnvironment[0]! : null;
}
